import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { from, of } from "rxjs";
import { catchError, concatMap, map, toArray } from "rxjs/operators";
import Swal from "sweetalert2";

import { MantenimientoService } from "../mantenimiento.service";
import {
  FOTO_MAX_MB,
  FOTOS_ACEPTADAS,
  FOTOS_MAX_POR_ETAPA,
  Mantenimiento,
  URGENCIAS_MANTENIMIENTO,
  urgenciaDe,
} from "../../shared/model/mantenimiento/mantenimiento.model";

/** Lo que avisa el formulario al terminar. */
export interface MantenimientoEnviado {
  idSolicitud: number;
  folio: string;
  /** Las fotos que no se pudieron subir (la solicitud sí quedó). */
  fotosFallidas: string[];
}

/** Desde el 15/10/2026, dos: «Qué y dónde» se juntó con los detalles. */
type Paso = "detalle" | "revisar";

interface FotoElegida {
  archivo: File;
  vista: string;
}

/**
 * Pedir mantenimiento, en tres pasos: qué y dónde, los detalles con sus
 * fotos, y revisar antes de enviar. Vive dentro del asistente «Nueva
 * solicitud»; la lista de «Mantenimiento» manda aquí con su botón.
 *
 * Primero se crea la solicitud y DESPUÉS se suben las fotos, una por una:
 * si una foto falla, la solicitud ya quedó y se dice cuál faltó (se puede
 * subir después desde el detalle).
 */
@Component({
  selector: "app-form-mantenimiento",
  standalone: true,
  templateUrl: "./form-mantenimiento.component.html",
  styleUrls: ["../mantenimiento-comun.css", "./form-mantenimiento.component.css"],
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule],
})
export class FormMantenimientoComponent implements OnInit, OnDestroy {
  @Output() enviada = new EventEmitter<MantenimientoEnviado>();
  @Output() volver = new EventEmitter<void>();
  /** Cancelar o Atrás, y Siguiente o Enviar: el concentrador los pone arriba, en su encabezado. */
  @ViewChild("acciones", { static: true }) acciones!: TemplateRef<unknown>;

  readonly URGENCIAS = URGENCIAS_MANTENIMIENTO;
  readonly ACEPTADAS = FOTOS_ACEPTADAS;
  readonly MAX_FOTOS = FOTOS_MAX_POR_ETAPA;

  readonly PASOS: { id: Paso; texto: string }[] = [
    { id: "detalle", texto: "Detalles y fotos" },
    { id: "revisar", texto: "Revisar y enviar" },
  ];

  paso: Paso = "detalle";

  /** El asunto (en el servidor, la descripción). Desde el 15/10/2026 no se pide tipo ni lugar. */
  descripcion = "";
  urgencia: string | null = "MEDIA";
  telefono = "";
  fotos: FotoElegida[] = [];

  /** A nombre de quién va, para el resumen. */
  yo = "";
  enviando = false;
  /** Si ya se intentó avanzar: a partir de ahí se marcan los campos vacíos. */
  intento = false;

  constructor(private service: MantenimientoService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.service.mias(true).subscribe({
      next: (res) => {
        this.yo = res?.yo || "";
        this.cdr.detectChanges();
      },
      error: () => {},
    });
  }

  ngOnDestroy(): void {
    this.fotos.forEach((f) => URL.revokeObjectURL(f.vista));
  }

  /* ---------------- Pasos ---------------- */

  indice(p: Paso = this.paso): number {
    return this.PASOS.findIndex((x) => x.id === p);
  }

  /** Lo que falta para salir del paso actual; null si nada. */
  get falta(): string | null {
    if (this.paso === "detalle") {
      if (!this.descripcion.trim()) return "Escribe el asunto.";
      if (!this.urgencia) return "Escoge qué tan urgente es.";
    }
    return null;
  }

  siguiente(): void {
    this.intento = true;
    if (this.falta) {
      return;
    }
    this.intento = false;
    this.paso = "revisar";
  }

  atras(): void {
    if (this.paso === "detalle") {
      this.volver.emit();
      return;
    }
    this.paso = "detalle";
  }

  irA(p: Paso): void {
    // Solo hacia atrás: hacia adelante se pasa por «Siguiente», que valida.
    if (this.indice(p) < this.indice()) {
      this.paso = p;
    }
  }

  /* ---------------- Fotos ---------------- */

  eligeFotos(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivos = Array.from(input.files || []);
    input.value = "";
    this.agregaFotos(archivos);
  }

  soltarFotos(ev: DragEvent): void {
    ev.preventDefault();
    this.arrastrando = false;
    this.agregaFotos(Array.from(ev.dataTransfer?.files || []));
  }

  arrastrando = false;

  private agregaFotos(archivos: File[]): void {
    const rechazadas: string[] = [];
    for (const a of archivos) {
      if (this.fotos.length >= this.MAX_FOTOS) {
        rechazadas.push(`${a.name} (ya van ${this.MAX_FOTOS})`);
        continue;
      }
      const ext = (a.name.split(".").pop() || "").toLowerCase();
      if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
        rechazadas.push(`${a.name} (no es JPG, PNG o WEBP)`);
        continue;
      }
      if (a.size > FOTO_MAX_MB * 1024 * 1024) {
        rechazadas.push(`${a.name} (pesa más de ${FOTO_MAX_MB} MB)`);
        continue;
      }
      this.fotos.push({ archivo: a, vista: URL.createObjectURL(a) });
    }
    if (rechazadas.length) {
      Swal.fire({
        icon: "warning",
        title: "Algunas fotos no se agregaron",
        html: '<div style="font-size:13.5px;color:#475569;text-align:left">' +
          rechazadas.map((r) => this.escapa(r)).join("<br>") + "</div>",
      });
    }
  }

  quitaFoto(i: number): void {
    const [f] = this.fotos.splice(i, 1);
    if (f) {
      URL.revokeObjectURL(f.vista);
    }
  }

  /* ---------------- Enviar ---------------- */

  enviar(): void {
    if (this.enviando) {
      return;
    }
    const solicitud: Mantenimiento = {
      urgencia: this.urgencia || undefined,
      descripcion: this.descripcion.trim(),
      telefonoContacto: this.telefono.trim() || null,
    };
    this.enviando = true;
    this.service.solicitar(solicitud).subscribe({
      next: (res) => {
        if (res?.codigoError || !res?.idGenerado) {
          this.enviando = false;
          this.aviso(res?.mensajeError || "No se pudo enviar la solicitud.");
          this.cdr.detectChanges();
          return;
        }
        this.subeFotos(res.idGenerado, res.folioGenerado || "");
      },
      error: () => {
        this.enviando = false;
        this.aviso("No se pudo conectar con el servidor.");
        this.cdr.detectChanges();
      },
    });
  }

  /** Una por una, para que una que falle no tumbe a las demás. */
  private subeFotos(idSolicitud: number, folio: string): void {
    from(this.fotos)
      .pipe(
        concatMap((f) =>
          this.service.subirFoto(idSolicitud, f.archivo).pipe(
            map((r) => (r?.codigoError ? f.archivo.name : null)),
            catchError(() => of(f.archivo.name)),
          ),
        ),
        toArray(),
      )
      .subscribe((resultado) => {
        this.enviando = false;
        const fotosFallidas = resultado.filter((n): n is string => !!n);
        this.enviada.emit({ idSolicitud, folio, fotosFallidas });
        this.cdr.detectChanges();
      });
  }

  /* ---------------- Apoyo de la plantilla ---------------- */

  get urgenciaElegida() {
    return urgenciaDe(this.urgencia);
  }

  tamano(bytes: number): string {
    return bytes >= 1024 * 1024 ? (bytes / 1024 / 1024).toFixed(1) + " MB" : Math.max(1, Math.round(bytes / 1024)) + " KB";
  }

  private aviso(texto: string): void {
    Swal.fire({ icon: "error", text: texto });
  }

  private escapa(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);
  }
}
